import React, {useState, useEffect, useCallback} from 'react';
import {useDispatch} from 'react-redux';
import {Button, Divider, Flex, Typography} from 'antd';
import {DownloadOutlined, FolderOpenTwoTone} from '@ant-design/icons';
import {ModelDTO} from 'hemwb-api';
import {useModelFileTreeTHLoader, TreeNode, modelFileItemDownload} from '../../../../store/modelFile';
import {modelDownload} from '../../../../store/model';
import {downloadFromResponse} from '../../../../utils/download';
import {trackModelItemDownloaded} from '../../../../tracking/tracking';
import {ModelFileDownloadModal} from './ModelFileDownloadModal';
import ModelFileManageAccessDialog from '../common/ModelOverview/ModelFileManageAccessDialog';
import ModelFileRequestAccessDialog from '../common/ModelOverview/ModelFileRequestAccessDialog';
import ModelFileManageOwnerAccessDialog from '../common/ModelOverview/ModelFileManageOwnerAccessDialog';
import {FileTreeNodeTH} from './FileTreeNodeTH';
import styles from './ModelFilesTab.module.scss';
//import {checkExternalSecurityAPI} from '../../../../permissions/usePermissions';
//import {PERMISSION} from '../../../../permissions/types';
import {messageAdd, MessageTypes} from '../../../../store/message';

interface ModelFilesTabTHProps {
  model: ModelDTO | null;
}

/**
 * THEOREM Model Files tab - displays THEOREM model files with CDD access control
 * Uses /treeauditedTH endpoint and includes file-level access management dialogs
 */
export const ModelFilesTabTH: React.FC<ModelFilesTabTHProps> = ({model}) => {
  const dispatch = useDispatch();

  // Always use TH endpoint for THEOREM models
  const {data: files, load: loadFiles} = useModelFileTreeTHLoader(model?.uuid || '');

  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [downloadType, setDownloadType] = useState<'all' | 'single'>('all');
  const [selectedFile, setSelectedFile] = useState<{uuid: string; name: string} | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Manage Access dialog state
  const [manageAccessDialogOpen, setManageAccessDialogOpen] = useState(false);
  const [requestAccessDialogOpen, setRequestAccessDialogOpen] = useState(false);
  const [ownershipDialogOpen, setOwnershipDialogOpen] = useState(false);
  const [selectedFileForAccess, setSelectedFileForAccess] = useState<{uuid: string; name: string} | null>(null);

  useEffect(() => {
    if (model?.uuid) {
      loadFiles();
    }
  }, [model?.uuid, loadFiles]);

  // THEOREM models: use checkExternalSecurityAPI for model-level permission
  //const canDownload = checkExternalSecurityAPI(model as ModelDetailDTO);

  const handleDownloadAll = () => {
    setDownloadType('all');
    setSelectedFile(null);
    setDownloadModalVisible(true);
  };

  const handleDownloadFile = (fileUuid: string, fileName: string) => {
    setDownloadType('single');
    setSelectedFile({uuid: fileUuid, name: fileName});
    setDownloadModalVisible(true);
  };

  const handleCloseModal = () => {
    setDownloadModalVisible(false);
    setSelectedFile(null);
  };

  const handleConfirmDownload = () => {
    handleCloseModal();
    setIsDownloading(true);

    if (downloadType === 'all' && model?.uuid) {
      // Download all files in the model
      modelDownload(dispatch, model.uuid)
        .then((response) => downloadFromResponse(response.raw))
        .catch(() => null)
        .finally(() => setIsDownloading(false));
    } else if (selectedFile && model?.uuid) {
      // Download individual file - track the download
      modelFileItemDownload(dispatch, model.uuid, selectedFile.uuid)
        .then((response) => {
          downloadFromResponse(response.raw);
          // Track individual file download
          trackModelItemDownloaded(model, selectedFile.name, selectedFile.uuid);
        })
        .catch(() => null)
        .finally(() => setIsDownloading(false));
    }
  };

  const handleManageAccess = (fileUuid: string, fileName: string) => {
    setSelectedFileForAccess({uuid: fileUuid, name: fileName});
    setManageAccessDialogOpen(true);
  };

  const handleManageAccessClose = () => {
    setManageAccessDialogOpen(false);
    setSelectedFileForAccess(null);
    // Reload files to refresh access states
    if (model?.uuid) {
      loadFiles();
    }
  };

  // Handle request access - shows error message with THEOREM access guide
  const handleRequestAccess = useCallback(
    (fileUuid: string, fileName: string) => {
      dispatch(
        messageAdd(
          `You don't have confidential Drug Discount access for this county in THEOREM. Please request access through the below link <a href="https://collaboration.merck.com/sites/THEOREM/Lists/FAQ%20%20THEOREM/DispForm.aspx?ID=53&e=mvH1Me">THEOREM Access Guide</a>
        For restricted models, confirm your access with the HEDS Lead.`,
          MessageTypes.ERROR,
        ),
      );
      // Optionally open dialog for tracking purposes
      setSelectedFileForAccess({uuid: fileUuid, name: fileName});
      // setRequestAccessDialogOpen(true);
    },
    [dispatch],
  );

  const handleRequestAccessClose = () => {
    setRequestAccessDialogOpen(false);
    setSelectedFileForAccess(null);
  };

  // Handle ownership management (for super users)
  const handleManageOwnership = (fileUuid: string, fileName: string) => {
    setSelectedFileForAccess({uuid: fileUuid, name: fileName});
    setOwnershipDialogOpen(true);
  };

  const handleOwnershipClose = () => {
    setOwnershipDialogOpen(false);
    setSelectedFileForAccess(null);
    // Reload files to refresh ownership states
    if (model?.uuid) {
      loadFiles();
    }
  };

  if (!model) {
    return <div>No model selected</div>;
  }

  // Transform THEOREM specification array to TreeNode-compatible format
  let rootItems: any[] = [];

  if (files && typeof files === 'object' && 'specification' in files) {
    rootItems = (files as any).specification.map((item: any) => ({
      ...item,
      uuid: item.uuid,
      name: item.fileName,
      folder: false,
      path: item.fileName,
      // Preserve THEOREM-specific properties for Access column and CDD logic
      owner: item.owner,
      cdd: item.cdd,
      userAccess: item.userAccess,
      isPrivate: item.isPrivate,
      updatedOn: item.createdOn,
      updatedBy: item.owner ? `${item.owner.lastname} ${item.owner.firstname}` : '',
      fileTag: item.fileTag,
    }));
  }

  const hasFiles = rootItems.length > 0;
  const mdpLink = model.metadata?.['model_development_plan'];
  const hasMdpLink = !!mdpLink && mdpLink.trim() !== '' && mdpLink.trim().toLowerCase() !== 'null';

  return (
    <div className={styles.modelFilesTab}>
      {/* Header with description and Download All button */}
      <div className={styles.header}>
        <span className={styles.description}>
          Browse and download THEOREM model files, documentation, and adaptations
        </span>
        <Button
          type="primary"
          style={{display: 'none'}} // Temporarily hide Download All button for THEOREM models until permissions are clarified
          icon={<DownloadOutlined />}
          onClick={handleDownloadAll}
          loading={isDownloading}
          disabled={isDownloading || !hasFiles /*|| canDownload !== PERMISSION.ALLOWED*/}
        >
          Download All
        </Button>
      </div>

      <Divider />

      {hasFiles ? (
        <>
          {/* File tree with THEOREM-specific Access column */}
          <FileTreeNodeTH
            files={rootItems as TreeNode[]}
            isRoot={true}
            defaultFolderOpen={true}
            onDownloadFile={handleDownloadFile}
            onManageAccess={handleManageAccess}
            onRequestAccess={handleRequestAccess}
            onManageOwnership={handleManageOwnership}
            model={model}
          />

          {/* MDP link if available */}
          {hasMdpLink && (
            <div style={{marginTop: '24px'}}>
              <strong>MDP: </strong>
              <a href={mdpLink} target="_blank" rel="noopener noreferrer">
                {mdpLink}
              </a>
            </div>
          )}
        </>
      ) : (
        <Flex vertical gap="10px" style={{alignItems: 'center', padding: '0 16px 16px'}}>
          <FolderOpenTwoTone twoToneColor={[styles.primaryTeal, styles.lockColor]} style={{fontSize: '39px'}} />
          <Typography.Text strong style={{fontSize: '16px'}}>
            No files available
          </Typography.Text>
          <Typography.Text type="secondary" style={{textAlign: 'center', maxWidth: '400px'}}>
            There are currently no files uploaded for this THEOREM model entry.
          </Typography.Text>
        </Flex>
      )}

      {/* Download confirmation modal */}
      <ModelFileDownloadModal
        visible={downloadModalVisible}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDownload}
        model={model}
        downloadType={downloadType}
        fileName={selectedFile?.name}
      />

      {/* THEOREM File Access Dialogs */}
      {selectedFileForAccess && (
        <>
          <ModelFileManageAccessDialog
            model={model}
            open={manageAccessDialogOpen}
            fileUuid={selectedFileForAccess.uuid}
            fileName={selectedFileForAccess.name}
            onSuccess={handleManageAccessClose}
            onCancel={handleManageAccessClose}
          />
          <ModelFileRequestAccessDialog
            model={model}
            open={requestAccessDialogOpen}
            fileUuid={selectedFileForAccess.uuid}
            fileName={selectedFileForAccess.name}
            onSuccess={handleRequestAccessClose}
            onCancel={handleRequestAccessClose}
          />
          <ModelFileManageOwnerAccessDialog
            model={model}
            open={ownershipDialogOpen}
            fileUuid={selectedFileForAccess.uuid}
            fileName={selectedFileForAccess.name}
            onSuccess={handleOwnershipClose}
            onCancel={handleOwnershipClose}
          />
        </>
      )}
    </div>
  );
};
